import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { getSpacingToken, FormMessage, IconName, Position, Stack, StackItem } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TooltippedIconButton } from '@mews-commander/ui';

interface LockableFieldProps {
    locked: boolean;
    tooltipTextKey: Mews.TextKey;
    fieldName?: string;
    children: (isLocked: boolean) => React.ReactNode;
    lockedReasonText: Mews.TextKey;
    onUnlock?: () => void;
}

const PositionedButton = styled(TooltippedIconButton)`
    margin-top: ${getSpacingToken('300')}px;
`;

export const LockableField = ({
    locked,
    tooltipTextKey,
    fieldName,
    children,
    lockedReasonText,
    onUnlock,
}: LockableFieldProps) => {
    const [isLocked, setIsLocked] = useState(locked);

    useEffect(() => {
        setIsLocked(locked);
    }, [locked]);

    const handleUnlock = () => {
        setIsLocked(false);

        onUnlock?.();
    };

    return (
        <>
            {locked ? (
                <Stack spacing="100" vertical>
                    <Stack
                        spacing="100"
                        distribution="space-between"
                        itemAlignment="start"
                        alignment="start"
                        data-test-id={fieldName ? 'lockable-field-' + fieldName : null}
                    >
                        <StackItem grow>{children(isLocked)}</StackItem>
                        <PositionedButton
                            icon={isLocked ? IconName.Lock : IconName.Unlock}
                            size="small"
                            disabled={!isLocked}
                            tooltip={<LocalizedText textKey={tooltipTextKey} />}
                            tooltipPosition={Position.Bottom}
                            onClick={handleUnlock}
                            type="button"
                        />
                    </Stack>

                    {isLocked ? (
                        <FormMessage>
                            <LocalizedText textKey={lockedReasonText} />
                        </FormMessage>
                    ) : null}
                </Stack>
            ) : (
                <>{children(locked)}</>
            )}
        </>
    );
};
